import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Seo } from '../components/Seo';
import { ShieldCheck } from 'lucide-react';

const S = ({ id, title, children }) => (
  <section id={id} className="mb-8 scroll-mt-24" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display text-xl font-semibold mb-2">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export default function Privacy() {
  const updated = 'June 2026';
  return (
    <Layout>
      <Seo title="Privacy Policy" path="/privacy"
        description="How FlightDeals.in collects, uses, shares and protects your data. Comparison metasearch — we never take payment; bookings complete on partner sites." />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 text-primary-foreground/80 text-sm mb-1"><ShieldCheck className="h-4 w-4" /> Legal</div>
          <h1 className="font-display text-3xl font-semibold">Privacy Policy</h1>
          <p className="text-primary-foreground/75 mt-1">Last updated: {updated}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="privacy-page">
        <p className="text-sm text-muted-foreground mb-8">
          FlightDeals.in ("FlightDeals", "we", "us") operates a travel <strong>metasearch and comparison</strong>
          service. We are not an airline, hotel, online travel agency or booking engine and we never take payment
          for travel — bookings, payments, confirmations, changes, cancellations and refunds are handled entirely
          by the third-party partners you are redirected to. This policy explains what data we process and your
          rights. By using the site you consent to the practices described here and in our{' '}
          <Link to="/cookies" className="text-accent hover:underline">Cookie Policy</Link>.
        </p>

        <S id="data-we-collect" title="1. Information we collect">
          <p>We collect the broadest set of data reasonably useful to operate, secure, improve and monetise the service, including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Search & usage data:</strong> origins, destinations, dates, travellers, cabin, filters, pages viewed, clicks, referrers, session and interaction events.</li>
            <li><strong>Device & network data:</strong> IP address, approximate location, device type, browser, operating system, language, and unique identifiers set via cookies and similar technologies.</li>
            <li><strong>Contact data you provide:</strong> email and message content when you use fare alerts, contact/help forms, or subscribe.</li>
            <li><strong>Account data (if you register):</strong> name, email, saved items and preferences.</li>
            <li><strong>Inferred data:</strong> interests, likely travel intent and segments derived from your activity.</li>
          </ul>
        </S>

        <S id="how-we-use" title="2. How we use your data">
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide, personalise and improve search results, pricing and recommendations.</li>
            <li>Send fare alerts and transactional emails you request, and — where permitted — marketing about deals and features.</li>
            <li>Measure performance, run analytics, A/B tests and fraud/abuse prevention.</li>
            <li><strong>Affiliate attribution & monetisation:</strong> track referrals and clicks to our partners so we earn commissions; this is core to our free service.</li>
            <li>Comply with law and enforce our terms.</li>
          </ul>
          <p>Our legal bases include your consent, our legitimate interests in operating and monetising the service, and, where applicable, performing a service you requested or complying with law.</p>
        </S>

        <S id="cookies" title="3. Cookies & tracking">
          <p>
            We and our partners use cookies, pixels, local storage, device fingerprinting and similar technologies for
            functionality, analytics, attribution and advertising. Continued use of the site with non-essential
            cookies enabled constitutes consent where consent is the applicable basis. See our full{' '}
            <Link to="/cookies" className="text-accent hover:underline">Cookie Policy</Link> for details and choices.
          </p>
        </S>

        <S id="sharing" title="4. How we share data">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Travel & affiliate partners</strong> (airlines, OTAs, hotel, tour, eSIM and insurance providers, and networks such as Travelpayouts/Aviasales, Agoda and Viator) — to complete redirects, enable bookings and attribute commissions.</li>
            <li><strong>Service providers</strong> that host, email, analyse or secure the service on our behalf.</li>
            <li><strong>Advertising & analytics providers</strong> for measurement and marketing.</li>
            <li><strong>Legal / corporate:</strong> to comply with law, respond to lawful requests, protect rights and safety, or in a merger, acquisition or asset sale.</li>
          </ul>
          <p>We do not sell your data for money, but some sharing for advertising may be considered a "sale" or "sharing" under certain laws (e.g. CCPA/CPRA) — see your rights below.</p>
        </S>

        <S id="international" title="5. International transfers & retention">
          <p>Your data may be processed in countries other than yours, including where privacy laws differ. We retain data for as long as needed for the purposes above, to comply with law, resolve disputes and enforce agreements, after which it is deleted or anonymised.</p>
        </S>

        <S id="rights" title="6. Your rights">
          <p>Depending on where you live (e.g. under GDPR, UK GDPR, India's DPDP Act, or CCPA/CPRA), you may have the right to access, correct, delete or port your data, object to or restrict processing, withdraw consent, and opt out of targeted advertising or "sale"/"sharing". To exercise these, contact us via the{' '}
            <Link to="/help" className="text-accent hover:underline">Help &amp; contact</Link> page. You may also disable cookies in your browser and unsubscribe from emails at any time. We will verify and respond as required by applicable law.</p>
        </S>

        <S id="children" title="7. Children">
          <p>The service is not directed to children under 16 and we do not knowingly collect their data. If you believe a child has provided data, contact us and we will delete it.</p>
        </S>

        <S id="security" title="8. Security">
          <p>We use reasonable technical and organisational measures to protect data, but no method of transmission or storage is completely secure and we cannot guarantee absolute security.</p>
        </S>

        <S id="changes" title="9. Changes & contact">
          <p>We may update this policy from time to time; the "Last updated" date reflects the latest version and continued use constitutes acceptance. Questions or requests: use our{' '}
            <Link to="/help" className="text-accent hover:underline">Help &amp; contact</Link> page.</p>
        </S>
      </div>
    </Layout>
  );
}
